/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { LuisConstants } from 'src/core/utils/LuisConstants';

/**
 * Implements a sliding window algorithm to limit the
 * requests at the given RPS.
 */
export const createRateLimiterInterceptor = (client: AxiosInstance, options?: { maxRPS?: number; skipThrottling?: boolean }) => {
    const runningTaskQueue: number[] = [];
    const queuedTasksQueue: (() => void)[] = [];

    const timeInterval = 1200;
    const intervalClock = 100;
    const skipThrottling = options?.skipThrottling;
    const maxRPS = options?.maxRPS ?? LuisConstants.ResourceLimits.ServiceRpsLimit;

    setInterval(() => {
        while (runningTaskQueue.length && Date.now() - runningTaskQueue[0] > timeInterval) {
            runningTaskQueue.shift();
        }

        while (queuedTasksQueue.length && runningTaskQueue.length < maxRPS) {
            const task = queuedTasksQueue.shift();
            runningTaskQueue.push(Date.now());
            task();
        }
    }, intervalClock);

    const handleRequest = async (request: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        if (skipThrottling || (<Record<string, string>>request.headers)['skip-throttling']) {
            return request;
        }

        if (runningTaskQueue.length < maxRPS) {
            runningTaskQueue.push(Date.now());
            return request;
        }

        // Queue the request if rate-limiting is in effect
        return new Promise<InternalAxiosRequestConfig>(resolve => {
            queuedTasksQueue.push(() => resolve(request));
        });
    };

    // Attach the request interceptor
    client.interceptors.request.use(handleRequest);
};
